import React, { RefObject } from 'react';
import {
    FormControlProps,
    UseEditableReturns,
    UseEditableProps,
    HStack,
    Text,
    Editable,
    FormControl,
    Box,
} from '@cad-arc';
import {
    ControllerProps,
    Controller,
    ControllerRenderProps,
    FieldPath,
    FieldValues,
    ControllerFieldState,
} from 'react-hook-form';

import { useFocusManager } from '@react-aria/focus';
import { HelpText } from '../../common/components/help-text';
import { useIntegratedForm } from '../use-integrated-form';
import composeRefs from '@seznam/compose-react-refs';
import cx from 'classnames';
import './style.css';
import { useFormInputHandleManagerScopeContext } from '../integrated-form-components/form-input-handle-manager-scope-context';
import { CadNextNotification, CadNextNotificationTypeEnumType } from '@mark43/cad-api';
import { observer } from 'mobx-react-lite';
import { eventNotificationPriorityToColors } from '../../event-notifications/event-notifications';
import {
    EDITABLE_BORDER_RADIUS_STYLES_WHEN_HAS_EVENT_NOTIFICATION,
    EventNotificationsFieldIndicator,
} from '../../event-notifications/event-notifications-field-indicator';
import { DataItemWithEventNotification } from '../../panels/entity-panels/entity-panel-components';
import { useFormEventNotificationControl } from '../use-form-event-notification-control';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { useEnabledFeatures } from '../../common/hooks/enabled-features';
import { logDebug } from '../../monitoring/log';

type CommonArcInputProps = {
    autoFocus?: boolean;
    placeholder?: string;
    isDisabled?: boolean;
    isLoading?: boolean;
    'aria-label'?: string;
    'data-testid'?: string;
    ref?: React.Ref<HTMLInputElement>;
    size?: React.ComponentProps<typeof FormControl>['size'];
    showRequiredIndicator?: boolean;
    tabIndex?: number;
    eventNotificationName?: CadNextNotificationTypeEnumType;
    mode?: 'read-only';
};

type InputHandlers = {
    onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
    onFocus?: React.FocusEventHandler<HTMLElement>;
    onBlur?: React.FocusEventHandler<HTMLElement>;
};

export type InputProps = Omit<
    ControllerRenderProps<any, string>,
    'onBlur' | 'onKeyDown' | 'onFocus' | 'ref'
> &
    CommonArcInputProps &
    InputHandlers;

type ViewModeRenderer<TValueType> = (value: TValueType) => React.ReactNode;

type IntegratedFormControlProps<TValueType, TElementType = HTMLInputElement> = {
    label?: string;
    controllerProps?: Partial<ControllerProps<any, any>>;
    helpTextTooltip?: string;
    helpText?: string;
    children: Children;
    length?: FormControlProps['length'];
    renderViewMode: ViewModeRenderer<TValueType>;
    cRef?: React.Ref<TElementType>; // https://fettblog.eu/typescript-react-generic-forward-refs/
    asEditable?: boolean;
    asDataListItem?: boolean;
    editableProps?: UseEditableProps & React.HTMLAttributes<HTMLDivElement>;
    onBlur?: (e: React.FocusEvent<HTMLElement, Element>) => void;
    onChange?: (value: React.ChangeEvent<HTMLElement>) => void;
    showLabel?: boolean;
    overrideFormSize?: boolean;
} & Pick<ControllerProps<FieldValues, FieldPath<FieldValues>>, 'name' | 'rules'> & {
        control: Exclude<ControllerProps<any, any>['control'], undefined>;
    } & CommonArcInputProps;

type Children = (inputProps: InputProps) => React.ReactElement;

export type CommonControlProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = Omit<
    IntegratedFormControlProps<unknown>,
    'children' | 'control' | 'name' | 'renderViewMode'
> & {
    control: Exclude<ControllerProps<TFieldValues, TName>['control'], undefined>;
    name: ControllerProps<TFieldValues, TName>['name'];
    renderViewMode?: ViewModeRenderer<any>;
    onBlur?: (e: React.FocusEvent<HTMLElement, Element>) => void;
    onChange?: (value: React.ChangeEvent<HTMLElement>) => void;
};

function wrappedRenderViewMode<T>(
    value: null | undefined | T,
    providedRenderViewMode: (val: T) => React.ReactNode
): React.ReactNode {
    if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value === '') ||
        (Array.isArray(value) && value.length === 0)
    ) {
        return <Text>&mdash;</Text>;
    }
    return providedRenderViewMode(value);
}

function isValueEmpty(value: unknown) {
    if (value) {
        /** Check to see if it's an empty array.
        Some values are stubbed as empty arrays but should be tabbable.*/
        if (Array.isArray(value)) {
            return value.length > 0 ? -1 : true;
        } else {
            return false;
        }
    } else {
        return true;
    }
}
export const IntegratedFormControl = observer(function IntegratedFormControl<T>({
    name,
    control,
    rules,
    label,
    size,
    controllerProps,
    cRef,
    children,
    renderViewMode,
    asEditable,
    editableProps,
    asDataListItem,
    eventNotificationName,
    showLabel,
    overrideFormSize,
    mode,
    ...innerProps
}: IntegratedFormControlProps<T>): React.ReactElement {
    const integratedForm = useIntegratedForm<FieldValues>();
    const controllerPropsMemo = React.useMemo(() => controllerProps ?? {}, [controllerProps]);
    const renderAsEditable =
        typeof asEditable !== 'undefined' ? asEditable : integratedForm.withEditable;
    const focusManager = useFocusManager();
    const { isFirstResponder } = useApplicationType();
    function moveFocusNext() {
        if (focusManager) {
            setTimeout(() => focusManager.focusNext({ tabbable: true }), 0);
        }
    }

    const { removeEventNotification, eventNotification } = useFormEventNotificationControl(
        eventNotificationName
    );
    const isReadOnly = mode === 'read-only' || integratedForm.mode === 'read-only';

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => {
                if (renderAsEditable || (isReadOnly && !asDataListItem)) {
                    return (
                        <HStack
                            alignItems="flex-start"
                            spacing={0}
                            style={{
                                gridColumn: `span ${
                                    innerProps.length === 'lg'
                                        ? '9'
                                        : innerProps.length === 'md'
                                        ? '6'
                                        : innerProps.length === 'sm'
                                        ? '3'
                                        : '12'
                                }`,
                            }}
                        >
                            {eventNotification ? (
                                <EventNotificationsFieldIndicator
                                    onClick={removeEventNotification}
                                />
                            ) : undefined}
                            <Editable
                                isPreviewInteractive={isReadOnly ? false : true}
                                tabIndex={isReadOnly ? -1 : isValueEmpty(field.value) ? 0 : -1}
                                editOnFocus={isReadOnly ? false : true}
                                inputProps={{
                                    toggleOnEnter: false,
                                    toggleOnEscape: isValueEmpty(field.value) ? false : true,
                                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                                        if (
                                            e.code === 'Enter' &&
                                            !(e.target instanceof HTMLTextAreaElement)
                                        ) {
                                            moveFocusNext();
                                        }
                                    },
                                }}
                                className={`${
                                    innerProps.length !== undefined
                                        ? 'integrated-form-control__editable--length-' +
                                          innerProps.length
                                        : 'integrated-form-control__editable'
                                }`}
                                style={{
                                    alignSelf: 'stretch',
                                    maxWidth: '100%',
                                    borderRadius: eventNotification
                                        ? EDITABLE_BORDER_RADIUS_STYLES_WHEN_HAS_EVENT_NOTIFICATION
                                        : undefined,
                                }}
                                {...editableProps}
                                editMode={(editableProps) => {
                                    return (
                                        <IntegratedFormControlInner
                                            field={field}
                                            fieldState={fieldState}
                                            formControlProps={{
                                                ...innerProps,
                                                asEditable: renderAsEditable,
                                                name,
                                                rules,
                                                label,
                                                size,
                                            }}
                                            editableProps={editableProps}
                                            cRef={cRef}
                                            inputSize={integratedForm.inputSize}
                                            eventNotification={eventNotification}
                                            showLabel={showLabel}
                                            overrideFormSize={overrideFormSize}
                                        >
                                            {children}
                                        </IntegratedFormControlInner>
                                    );
                                }}
                                viewMode={(editableProps) => {
                                    return (
                                        <IntegratedFormControlInner
                                            field={field}
                                            fieldState={fieldState}
                                            formControlProps={{
                                                ...innerProps,
                                                asEditable: renderAsEditable,
                                                name,
                                                rules,
                                                label,
                                                size,
                                            }}
                                            cRef={cRef}
                                            editableProps={editableProps}
                                            inputSize={integratedForm.inputSize}
                                            eventNotification={eventNotification}
                                            showLabel={showLabel}
                                            overrideFormSize={overrideFormSize}
                                        >
                                            <Box
                                                style={{
                                                    fontSize: isFirstResponder
                                                        ? 'var(--arc-fontSizes-lg)'
                                                        : 'var(--arc-fontSizes-sm)',
                                                    wordBreak: 'break-word',
                                                    fontWeight: 'var(--arc-fontWeights-semibold)',
                                                }}
                                                data-testid="editable-view-text"
                                            >
                                                {wrappedRenderViewMode(field.value, renderViewMode)}
                                            </Box>
                                        </IntegratedFormControlInner>
                                    );
                                }}
                            />
                        </HStack>
                    );
                } else if (asDataListItem) {
                    return (
                        <RegisterFormControl name={field.name}>
                            <DataItemWithEventNotification
                                dataKey={label}
                                eventNotificationName={eventNotificationName}
                            >
                                {wrappedRenderViewMode(field.value, renderViewMode)}
                            </DataItemWithEventNotification>
                        </RegisterFormControl>
                    );
                } else {
                    return (
                        <IntegratedFormControlInner
                            field={field}
                            fieldState={fieldState}
                            formControlProps={{
                                ...innerProps,
                                asEditable: renderAsEditable,
                                name,
                                rules,
                                label,
                                size,
                            }}
                            inputSize={integratedForm.inputSize}
                            cRef={cRef}
                            showLabel={showLabel}
                            overrideFormSize={overrideFormSize}
                        >
                            {children}
                        </IntegratedFormControlInner>
                    );
                }
            }}
            {...controllerPropsMemo}
        />
    );
});

const RegisterFormControl = ({
    children,
    name,
    inputRef,
    editableProps,
}: React.PropsWithChildren<{
    name: string;
    inputRef?: RefObject<HTMLInputElement>;
    editableProps?: Omit<UseEditableReturns, 'containerProps'>;
}>) => {
    const inputHandleManagerScope = useFormInputHandleManagerScopeContext();
    const integratedForm = useIntegratedForm<FieldValues>();
    React.useEffect(() => {
        integratedForm.inputHandleManager.registerHandle(name, {
            ref: inputRef,
            setEditableMode: editableProps?.toggleMode,
            scope: inputHandleManagerScope,
        });
        return () => {
            integratedForm.inputHandleManager.unregisterHandle(name);
        };
    }, [
        name,
        editableProps?.toggleMode,
        inputRef,
        integratedForm.inputHandleManager,
        inputHandleManagerScope,
    ]);

    return <>{children}</>;
};

function IntegratedFormControlInner<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: {
    children: Children | React.ReactNode | React.ReactElement;
    formControlProps: Omit<
        IntegratedFormControlProps<unknown>,
        'children' | 'control' | 'controllerProps' | 'cRef' | 'renderViewMode'
    >;
    field: ControllerRenderProps<TFieldValues, TName>;
    fieldState: ControllerFieldState;
    editableProps?: Omit<UseEditableReturns, 'containerProps'>;
    cRef?: React.Ref<HTMLInputElement>;
    inputSize: InputProps['size'];
    className?: string;
    eventNotification?: CadNextNotification;
    showLabel?: boolean;
    overrideFormSize?: boolean;
}): React.ReactElement {
    const enabledFeatures = useEnabledFeatures();
    const integratedForm = useIntegratedForm();
    const {
        field,
        fieldState,
        formControlProps,
        editableProps,
        children,
        cRef,
        inputSize,
        className,
        eventNotification,
        showLabel = true, //TODO: Reassess defaulting to true here. If there is no label, extra space gets added above the component
        overrideFormSize = false,
    } = props;
    const rolloutDisableIntegratedFormAutoFocus =
        enabledFeatures.rolloutDisableIntegratedFormAutoFocus;
    const editableInputProps: undefined | (InputHandlers & { ref: CommonArcInputProps['ref'] }) =
        editableProps?.inputProps;
    const fieldOnChange = field.onChange;
    const formControlPropsOnChange = formControlProps.onChange;
    const onChange = React.useCallback(
        (e) => {
            fieldOnChange(e);
            formControlPropsOnChange?.(e);
        },
        [fieldOnChange, formControlPropsOnChange]
    );
    const onFocus = editableInputProps?.onFocus;
    const onKeyDown = editableInputProps?.onKeyDown;
    const name = field.name;
    const fieldOnBlur = field.onBlur;
    const basicInputRenderProps: Pick<
        InputProps,
        | 'autoFocus'
        | 'placeholder'
        | 'isDisabled'
        | 'isLoading'
        | 'aria-label'
        | 'data-testid'
        | 'name'
        | 'tabIndex'
    > = React.useMemo(() => {
        return {
            autoFocus: formControlProps.autoFocus,
            placeholder: formControlProps.placeholder,
            isLoading: formControlProps.isLoading,
            ...integratedForm.getInputProps({
                isDisabled: formControlProps.isDisabled,
            }),
            'aria-label': formControlProps['aria-label'] ?? formControlProps.label ?? '',
            'data-testid': formControlProps['data-testid'] ?? name,
            name,
        };
    }, [formControlProps, integratedForm, name]);
    const editableInputPropsOnBlur = editableInputProps?.onBlur;
    const formControlPropsOnBlur = formControlProps.onBlur;
    const onBlur: React.FocusEventHandler<HTMLElement> = React.useMemo(() => {
        return async (e) => {
            if (formControlProps.asEditable) {
                fieldOnBlur();
                formControlPropsOnBlur?.(e);
                logDebug(
                    `[FOCUS DEBUG] integrated-form-control onBlur calling form.trigger with shouldFocus=${rolloutDisableIntegratedFormAutoFocus} for field: ${name}`
                );
                const result = await integratedForm.form.trigger(name, {
                    shouldFocus: rolloutDisableIntegratedFormAutoFocus,
                });
                if (result) {
                    logDebug(
                        `[FOCUS DEBUG] integrated-form-control form.trigger succeeded, calling editableInputPropsOnBlur for field: ${name}`
                    );
                    editableInputPropsOnBlur?.(e);
                }
            } else {
                formControlPropsOnBlur?.(e);
                fieldOnBlur();
            }
        };
    }, [
        formControlProps.asEditable,
        integratedForm.form,
        name,
        fieldOnBlur,
        editableInputPropsOnBlur,
        formControlPropsOnBlur,
        rolloutDisableIntegratedFormAutoFocus,
    ]);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const composedRef = composeRefs(cRef, inputRef, editableInputProps?.ref);
    const toggleMode = editableProps?.toggleMode;
    const editableMode = editableProps?.state.mode;
    const currentSize = overrideFormSize ? undefined : inputSize ?? formControlProps.size;

    React.useEffect(() => {
        if (fieldState.error && editableMode === 'view' && toggleMode) {
            toggleMode('edit');
        }
    }, [fieldState.error, toggleMode, editableMode]);

    React.useEffect(() => {
        if (!rolloutDisableIntegratedFormAutoFocus) {
            if (formControlProps.autoFocus) {
                logDebug(
                    `[FOCUS DEBUG] integrated-form-control useEffect calling inputRef.focus() for field: ${name}`
                );
                inputRef?.current?.focus();
            }
        }
    }, [formControlProps.autoFocus, rolloutDisableIntegratedFormAutoFocus, name]);

    return (
        <RegisterFormControl name={field.name} editableProps={editableProps} inputRef={inputRef}>
            <FormControl
                tabIndex={formControlProps.tabIndex}
                className={cx(
                    {
                        'integrated-form-control__label--size-sm': currentSize === 'sm',
                        'integrated-form-control__label--mode-view': editableMode === 'view',
                    },
                    className
                )}
                size={currentSize}
                length={formControlProps.length}
                htmlFor={field.name}
                helpText={formControlProps.helpText}
                label={
                    showLabel ? (
                        <span
                            style={{
                                fontWeight: eventNotification ? 700 : undefined,
                                color: eventNotification
                                    ? eventNotificationPriorityToColors[
                                          eventNotification.notificationPriority
                                      ].notchOrText
                                    : editableMode === 'view'
                                    ? 'text.tertiary'
                                    : undefined,
                            }}
                        >
                            {formControlProps.helpTextTooltip ? (
                                <>
                                    {formControlProps.label}
                                    <HelpText
                                        placement="top"
                                        overlay={formControlProps.helpTextTooltip}
                                    />
                                </>
                            ) : (
                                formControlProps.label
                            )}
                        </span>
                    ) : undefined
                }
                isRequired={
                    showLabel &&
                    editableProps?.state.mode !== 'view' &&
                    (formControlProps.showRequiredIndicator ||
                        (!!formControlProps.label && !!formControlProps.rules?.required))
                }
                errorMessage={fieldState.error?.message}
                isInvalid={!!fieldState.error?.message}
                isDisabled={formControlProps.isDisabled}
            >
                {typeof children === 'function'
                    ? children({
                          ...basicInputRenderProps,
                          value: field.value,
                          ref: composedRef,
                          onChange,
                          onFocus,
                          onKeyDown,
                          onBlur,
                          size: inputSize,
                      })
                    : children}
            </FormControl>
        </RegisterFormControl>
    );
}
